import { Edit, Delete, Error as ErrorIcon } from '@mui/icons-material';
import { Button, CardContainer, Tag } from 'finara-components';
import { useState } from 'react';

import { BulkPaymentValidation, ParsedPaymentRow } from '@/types/payment';

interface BulkPreviewProps {
  validation: BulkPaymentValidation;
  onUpdateValidation: (validation: BulkPaymentValidation) => void;
  onNext: () => void;
}

export const BulkPreview: React.FC<BulkPreviewProps> = ({
  validation,
  onUpdateValidation,
  onNext,
}) => {
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<ParsedPaymentRow>>({});

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount);
  };

  const handleEditStart = (payment: ParsedPaymentRow) => {
    setEditingRow(payment.id);
    setEditData({
      beneficiario: payment.beneficiario,
      email: payment.email,
      clabe: payment.clabe,
      rfc: payment.rfc,
      monto: payment.monto,
      concepto: payment.concepto,
      fecha: payment.fecha,
    });
  };

  const handleEditSave = () => {
    if (!editingRow) return;

    const updatedPayments = validation.payments.map(payment => {
      if (payment.id === editingRow) {
        const updatedPayment = { ...payment, ...editData };

        // Re-validate the updated payment
        const errors: string[] = [];

        if (!updatedPayment.beneficiario.trim()) errors.push('Beneficiario requerido');
        if (updatedPayment.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(updatedPayment.email))
          errors.push('Email inválido');
        if (!updatedPayment.clabe || updatedPayment.clabe.length !== 18)
          errors.push('CLABE inválido');
        if (updatedPayment.rfc && !/^[A-Z&Ñ]{3,4}\d{6}[A-Z\d]{3}$/.test(updatedPayment.rfc))
          errors.push('RFC inválido');
        if (!updatedPayment.monto || updatedPayment.monto <= 0)
          errors.push('Monto debe ser mayor a 0');
        if (!updatedPayment.concepto.trim()) errors.push('Concepto requerido');
        if (
          updatedPayment.fecha &&
          !/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/.test(updatedPayment.fecha)
        )
          errors.push('Fecha inválida');

        updatedPayment.isValid = errors.length === 0;
        updatedPayment.errors = errors;

        return updatedPayment;
      }
      return payment;
    });

    // Recalculate summary
    const validPayments = updatedPayments.filter(p => p.isValid);
    const totalAmount = validPayments.reduce((sum, p) => sum + p.monto, 0);
    const commission = totalAmount * 0.0015;

    const updatedValidation: BulkPaymentValidation = {
      ...validation,
      validRows: validPayments.length,
      invalidRows: updatedPayments.length - validPayments.length,
      payments: updatedPayments,
      batchSummary: {
        totalAmount,
        totalCommission: commission,
        totalWithCommission: totalAmount + commission,
      },
    };

    onUpdateValidation(updatedValidation);
    setEditingRow(null);
    setEditData({});
  };

  const handleEditCancel = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleDelete = (paymentId: string) => {
    const updatedPayments = validation.payments.filter(p => p.id !== paymentId);

    const validPayments = updatedPayments.filter(p => p.isValid);
    const totalAmount = validPayments.reduce((sum, p) => sum + p.monto, 0);
    const commission = totalAmount * 0.0015;

    const updatedValidation: BulkPaymentValidation = {
      ...validation,
      totalRows: updatedPayments.length,
      validRows: validPayments.length,
      invalidRows: updatedPayments.length - validPayments.length,
      payments: updatedPayments,
      batchSummary: {
        totalAmount,
        totalCommission: commission,
        totalWithCommission: totalAmount + commission,
      },
    };

    onUpdateValidation(updatedValidation);
  };

  const validPayments = validation.payments.filter(p => p.isValid);
  const invalidPayments = validation.payments.filter(p => !p.isValid);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-brand-white mb-2">Previsualización del Lote</h2>
        <p className="text-muted">Revisa y edita los pagos antes de procesar</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:!grid-cols-4 gap-6 mb-8">
        <CardContainer className="p-6 text-center">
          <div className="text-2xl font-bold text-brand-white">{validation.totalRows}</div>
          <div className="text-sm text-muted">Pagos a Procesar</div>
        </CardContainer>

        <CardContainer className="p-6 text-center">
          <div className="text-2xl font-bold text-brand-white">
            {formatCurrency(validation.batchSummary.totalAmount)}
          </div>
          <div className="text-sm text-muted">Total del Lote</div>
        </CardContainer>
      </div>

      {/* Valid Payments */}
      {validPayments.length > 0 && (
        <CardContainer className="mb-8">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-brand-white mb-4">Pagos Válidos</h3>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Beneficiario
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Email</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">CLABE</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">RFC</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Monto</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Concepto
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Fecha y Hora
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {validPayments.map(payment => (
                    <tr key={payment.id} className="border-b border-border">
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.beneficiario || ''}
                            onChange={e =>
                              setEditData({ ...editData, beneficiario: e.target.value })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.beneficiario
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="email"
                            value={editData.email || ''}
                            onChange={e => setEditData({ ...editData, email: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            placeholder="opcional"
                          />
                        ) : (
                          payment.email || '-'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm font-mono">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.clabe || ''}
                            onChange={e => setEditData({ ...editData, clabe: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            maxLength={18}
                          />
                        ) : (
                          payment.clabe
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.rfc || ''}
                            onChange={e =>
                              setEditData({ ...editData, rfc: e.target.value.toUpperCase() })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.rfc || '-'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="number"
                            value={editData.monto || ''}
                            onChange={e =>
                              setEditData({ ...editData, monto: parseFloat(e.target.value) })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            step="0.01"
                            min="0"
                          />
                        ) : (
                          formatCurrency(payment.monto)
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.concepto || ''}
                            onChange={e => setEditData({ ...editData, concepto: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.concepto
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="datetime-local"
                            value={editData.fecha?.replace(' ', 'T') || ''}
                            onChange={e => {
                              // Convert datetime-local format back to our format
                              const value = e.target.value.replace('T', ' ');
                              setEditData({ ...editData, fecha: value });
                            }}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.fecha || '-'
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {editingRow === payment.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={handleEditSave}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={handleEditCancel}
                              className="text-muted hover:text-brand-white text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditStart(payment)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit fontSize="small" />
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Delete fontSize="small" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContainer>
      )}

      {/* Invalid Payments */}
      {invalidPayments.length > 0 && (
        <CardContainer className="mb-8">
          <div className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <h3 className="text-lg font-semibold text-brand-white">Pagos Inválidos</h3>
              <Tag variant="red">{invalidPayments.length}</Tag>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Fila</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Beneficiario
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Email</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">CLABE</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">RFC</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">Monto</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Concepto
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Fecha y Hora
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Errores
                    </th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-brand-white">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invalidPayments.map(payment => (
                    <tr key={payment.id} className="border-b border-border bg-red-500/10">
                      <td className="py-3 px-2 text-sm font-medium">row-{payment.row}</td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.beneficiario || ''}
                            onChange={e =>
                              setEditData({ ...editData, beneficiario: e.target.value })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.beneficiario || 'Sin nombre'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="email"
                            value={editData.email || ''}
                            onChange={e => setEditData({ ...editData, email: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            placeholder="opcional"
                          />
                        ) : (
                          payment.email || '-'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.clabe || ''}
                            onChange={e => setEditData({ ...editData, clabe: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            maxLength={18}
                          />
                        ) : (
                          payment.clabe || 'INVALID'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.rfc || ''}
                            onChange={e =>
                              setEditData({ ...editData, rfc: e.target.value.toUpperCase() })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.rfc || '-'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="number"
                            value={editData.monto || ''}
                            onChange={e =>
                              setEditData({ ...editData, monto: parseFloat(e.target.value) })
                            }
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                            step="0.01"
                            min="0"
                          />
                        ) : payment.monto ? (
                          formatCurrency(payment.monto)
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="text"
                            value={editData.concepto || ''}
                            onChange={e => setEditData({ ...editData, concepto: e.target.value })}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.concepto || 'Sin concepto'
                        )}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        {editingRow === payment.id ? (
                          <input
                            type="datetime-local"
                            value={editData.fecha?.replace(' ', 'T') || ''}
                            onChange={e => {
                              const value = e.target.value.replace('T', ' ');
                              setEditData({ ...editData, fecha: value });
                            }}
                            className="w-full px-2 py-1 border border-border bg-surface text-brand-white rounded text-sm"
                          />
                        ) : (
                          payment.fecha || '-'
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <div className="space-y-1">
                          {payment.errors.map((error, index) => (
                            <Tag key={index} variant="red">
                              {error}
                            </Tag>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {editingRow === payment.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={handleEditSave}
                              className="text-green-600 hover:text-green-800 text-sm"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={handleEditCancel}
                              className="text-muted hover:text-brand-white text-sm"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditStart(payment)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit fontSize="small" />
                            </button>
                            <button
                              onClick={() => handleDelete(payment.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Delete fontSize="small" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start space-x-2">
                <ErrorIcon className="text-yellow-400 mt-0.5" fontSize="small" />
                <div>
                  <div className="font-medium text-brand-white">Tip:</div>
                  <div className="text-sm text-muted">
                    Puedes editar los pagos inválidos directamente haciendo clic en el ícono de
                    edición, o eliminarlos. Solo se procesarán los pagos válidos cuando continúes.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContainer>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <Button
          variant="black"
          size="large"
          onClick={onNext}
          disabled={validPayments.length === 0}
          className="min-w-32"
        >
          Autorizar Pagos
        </Button>
      </div>
    </div>
  );
};
